import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import BrandLogo from '../common/BrandLogo';

const SidebarItem = ({ item, collapsed, setCollapsed, activePath }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;
  
  const isChildActive = hasChildren && item.children.some(child => activePath === child.path);
  const isExpanded = isOpen || isChildActive;

  const handleParentClick = () => {
    if (collapsed) {
      setCollapsed(false);
      setIsOpen(true);
    } else {
      setIsOpen(!isOpen);
    }
  };

  if (hasChildren) {
    return (
      <div className="flex flex-col w-full mb-1">
        <button
          type="button"
          onClick={handleParentClick}
          className={`w-full font-bold uppercase tracking-wider text-[11px] transition-all duration-200 border-b border-white/5 select-none rounded-r-md ${
            isExpanded
              ? 'bg-white/10 text-white border-l-4 border-red-500 shadow-sm'
              : 'bg-transparent text-slate-200 hover:text-white hover:bg-white/10 border-l-4 border-transparent'
          } ${
            collapsed
              ? 'flex items-center justify-center py-2.5 px-2'
              : 'px-3.5 py-2 flex items-center justify-between text-left'
          }`}
          title={collapsed ? item.title : undefined}
        >
          <div className="flex items-center gap-3 min-w-0">
            {item.icon && (
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                <span className={`material-icons leading-none transition-colors ${
                  collapsed ? 'text-xl text-white' : 'text-lg text-slate-300'
                }`}>
                  {item.icon}
                </span>
              </div>
            )}
            {!collapsed && <span className="animate-fade-in truncate font-bold">{item.title}</span>}
          </div>
          {!collapsed && (
            <span className={`material-icons text-slate-400 select-none text-base leading-none transition-transform duration-300 shrink-0 ${
              isExpanded ? 'rotate-180 text-white' : ''
            }`}>
              expand_more
            </span>
          )}
        </button>

        {/* Submenu animation container */}
        <div
          className={`grid transition-all duration-300 ease-in-out ${
            isExpanded && !collapsed ? 'grid-rows-[1fr] opacity-100 mt-1' : 'grid-rows-[0fr] opacity-0 overflow-hidden'
          }`}
        >
          <div className="overflow-hidden">
            <div className="flex flex-col bg-black/20 border-b border-white/5 py-1 pl-4 pr-1.5 space-y-1 ml-5 border-l border-white/10">
              {item.children.map(child => (
                <NavLink
                  key={child.id}
                  to={child.path}
                  className={({ isActive: isSubActive }) =>
                    `block px-3 py-1.5 text-[12.5px] font-medium text-left select-none transition-all duration-150 rounded-r-md ${
                      isSubActive
                        ? 'bg-red-600 text-white font-bold border-l-4 border-white shadow-md shadow-red-950/50 pl-3'
                        : 'text-slate-300 hover:text-white hover:bg-white/10 border-l-2 border-transparent pl-3'
                    }`
                  }
                >
                  {child.title}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <NavLink
      to={item.path}
      className={({ isActive: isItemActive }) =>
        `text-xs font-bold uppercase tracking-wider select-none border-b border-white/5 transition-all duration-150 rounded-r-md ${
          isItemActive
            ? 'bg-red-600 text-white font-bold border-l-4 border-white shadow-md shadow-red-950/50'
            : 'bg-transparent text-slate-200 hover:bg-white/10 hover:text-white border-l-4 border-transparent'
        } ${
          collapsed
            ? 'flex items-center justify-center py-2.5 px-2'
            : 'block px-3.5 py-2 text-left'
        }`
      }
      title={collapsed ? item.title : undefined}
    >
      <div className="flex items-center gap-3">
        {item.icon && (
          <div className="w-5 h-5 flex items-center justify-center shrink-0">
            <span className={`material-icons leading-none transition-colors ${
              collapsed ? 'text-xl text-white' : 'text-lg text-slate-300'
            }`}>
              {item.icon}
            </span>
          </div>
        )}
        {!collapsed && <span className="animate-fade-in truncate">{item.title}</span>}
      </div>
    </NavLink>
  );
};

export const Sidebar = ({ menus = [], collapsed, setCollapsed, mobileOpen, setMobileOpen }) => {
  const location = useLocation();

  return (
    <>
      {/* Mobile Sidebar Backdrop Overlay */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 z-30 bg-slate-950/70 backdrop-blur-sm md:hidden transition-opacity duration-300"
        />
      )}

      <aside
        className={`fixed top-0 left-0 z-40 h-screen max-h-screen bg-dark-navy border-r border-white/10 flex flex-col transition-all duration-300 theme-transition overflow-hidden 
          ${collapsed ? 'md:w-20' : 'md:w-[272px]'} 
          w-[272px]
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
      >
        {/* Header Branding */}
        <div className={`flex items-center h-[72px] border-b border-white/10 shrink-0 bg-dark-navy ${
          collapsed ? 'justify-center' : 'justify-between px-3.5'
        }`}>
          <BrandLogo collapsed={collapsed} />
        </div>

        {/* Scrollable Navigation */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden flex flex-col scroll-smooth sidebar-scrollbar bg-dark-navy py-2 px-1.5 space-y-2 pb-8">
          {menus.map((item) => (
            <SidebarItem
              key={item.id}
              item={item}
              collapsed={collapsed}
              setCollapsed={setCollapsed}
              activePath={location.pathname}
            />
          ))}
        </nav>

        {/* Bottom Toggle Button */}
        <div className="p-3 border-t border-white/10 shrink-0 md:flex hidden items-center justify-center bg-dark-navy pb-5">
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-red-600 bg-red-600 hover:bg-red-700 hover:border-red-700 text-white transition-all duration-200 shadow-md shadow-red-950/50 hover:scale-105 active:scale-95"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <span className="material-icons text-lg">
              {collapsed ? 'first_page' : 'last_page'}
            </span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
